/**
 * Custom web component for displaying wishlist notifications.
 * Shows when products are added/removed from wishlist.
 * @extends HTMLElement
 */
export class WishlistNotification extends HTMLElement {
  constructor() {
    super();

    /**
     * DOM element references
     * @type {{
     *   template: HTMLElement,
     *   closeBtn: Element|null,
     *   title: Element|null,
     *   copy: Element|null,
     *   cta: Element|null
     * }}
     */
    this.dom = {
      template: document.querySelector('body'),
      closeBtn: this.querySelector('.js-wishlist-close'),
      title: this.querySelector('.js-wishlist-title'),
      copy: this.querySelector('.js-wishlist-copy'),
      cta: this.querySelector('.js-wishlist-cta'),
    };

    /**
     * Timeout ID for auto-hide functionality
     * @type {number|null}
     */
    this.hideTimeout = null;

    /**
     * Remaining time before auto-hide (in milliseconds)
     * @type {number}
     */
    this.remainingTime = 0;

    /**
     * Timestamp when timeout was started
     * @type {number}
     */
    this.startTime = 0;

    this.handleClose = this.close.bind(this);
    this.handleMouseEnter = this.pauseTimeout.bind(this);
    this.handleMouseLeave = this.resumeTimeout.bind(this);
  }
  connectedCallback() {
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);

    if (this.dom?.closeBtn) {
      this.dom.closeBtn.addEventListener('click', this.handleClose);
    }
  }
  disconnectedCallback() {
    if (this.dom?.closeBtn) {
      this.dom.closeBtn.removeEventListener('click', this.handleClose);
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  /**
   * Shows the notification with product info
   * Displays notification, updates content, and sets auto-hide timer
   * @param {string} action - Action performed ('added' or 'removed')
   * @param {string} productTitle - Title of the product
   */
  show(action, productTitle) {
    const { title, copy, template, cta } = this.dom;

    template.classList.add('wishlist-notification--active');

    if (title) title.textContent = productTitle;

    if (copy) {
      const proposition =
        action === 'removed' ? `${action} from` : `${action} to`;
      const message = `has been ${proposition} your wishlist.`;
      copy.textContent = message;
    }

    cta.classList.toggle(
      'WishlistNotification__Cta--Hide',
      action === 'removed'
    );

    // Disable all wishlist buttons
    this.disableWishlistButtons(true);

    // Auto-hide after 4 seconds
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    this.remainingTime = 4000;
    this.startTime = Date.now();
    this.hideTimeout = setTimeout(() => this.close(), this.remainingTime);
  }

  /**
   * Pauses the auto-hide timeout when user hovers over notification
   * Calculates and stores remaining time
   */
  pauseTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.remainingTime -= Date.now() - this.startTime;
      this.hideTimeout = null;
    }
  }

  /**
   * Resumes the auto-hide timeout when user stops hovering
   * Restarts timer with remaining time
   */
  resumeTimeout() {
    if (this.remainingTime > 0) {
      this.startTime = Date.now();
      this.hideTimeout = setTimeout(() => this.close(), this.remainingTime);
    }
  }

  /**
   * Closes the notification and re-enables buttons
   * Clears timeout, removes active class, and clears content after animation
   */
  close() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    const { title, copy, template } = this.dom;

    template.classList.remove('wishlist-notification--active');

    setTimeout(() => {
      if (title) title.textContent = '';
      if (copy) copy.textContent = '';
      this.disableWishlistButtons(false);
    }, 300);
  }

  /**
   * Enables or disables all wishlist buttons on the page
   * @param {boolean} disabled - Whether to disable the buttons
   */
  disableWishlistButtons(disabled) {
    const buttons = document.querySelectorAll('wishlist-btn');
    buttons.forEach((btn) => {
      btn.setAttribute('aria-disabled', `${disabled}`);
    });
  }
}

customElements.define('wishlist-notification', WishlistNotification);
